import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Clock, CheckCircle, Plus, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import { StatusBadge, ServiceIcon, LoadingSpinner } from '../common/Badges';
import { useLang } from '../../i18n';

const CustomerDashboard = () => {
  const { t, lang } = useLang();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [smsOptedOut, setSmsOptedOut] = useState(false);
  const [smsBusy, setSmsBusy] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const data = await api('/api/service-requests');
      setRequests(data);
      const me = JSON.parse(localStorage.getItem('user') || '{}');
      if (me.phone) {
        try {
          const s = await api(`/api/sms/status?phone=${encodeURIComponent(me.phone)}`);
          setSmsOptedOut(!!s.opted_out);
        } catch { /* ignore */ }
      }
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleSmsPreference = async () => {
    const me = JSON.parse(localStorage.getItem('user') || '{}');
    if (!me.phone) {
      toast.error('Add a phone number to your account first to manage SMS preferences');
      return;
    }
    setSmsBusy(true);
    try {
      const endpoint = smsOptedOut ? '/api/sms/opt-in' : '/api/sms/opt-out';
      const res = await api(endpoint, { method: 'POST', body: JSON.stringify({ phone: me.phone }) });
      if (res.opted_in || res.opted_out !== undefined) {
        const newOut = smsOptedOut ? false : true;
        setSmsOptedOut(newOut);
        toast.success(newOut ? 'SMS notifications turned OFF' : 'SMS notifications turned ON');
      }
    } catch (err) { toast.error(err.message); }
    finally { setSmsBusy(false); }
  };

  const stats = [
    { label: lang === 'sq' ? 'Kërkesat Gjithsej' : lang === 'es' ? 'Solicitudes Totales' : 'Total Requests', value: requests.length, icon: FileText, color: 'bg-red-600' },
    { label: lang === 'sq' ? 'Në Proces' : lang === 'es' ? 'En Progreso' : 'In Progress', value: requests.filter(r => ['assigned', 'in_progress'].includes(r.status)).length, icon: Clock, color: 'bg-gray-600' },
    { label: lang === 'sq' ? 'Të Përfunduara' : lang === 'es' ? 'Completadas' : 'Completed', value: requests.filter(r => r.status === 'completed').length, icon: CheckCircle, color: 'bg-green-600' }
  ];

  if (loading) return <LoadingSpinner />;

  return (
    <div data-testid="customer-dashboard" className="space-y-8">
      <h1 className="font-heading text-3xl font-bold text-white">{lang === 'sq' ? 'Paneli i Klientit' : lang === 'es' ? 'Panel del Cliente' : 'Customer Dashboard'}</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat) => (
          <div key={stat.label} className="card p-6 bg-gray-900 border-gray-800">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 ${stat.color} flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stat.value}</p>
                <p className="text-sm text-gray-400">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card bg-gray-900 border-gray-800" data-testid="sms-preference-card">
        <div className="p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0">
              <MessageSquare className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-sm font-bold text-white">{lang === 'sq' ? 'Njoftime me SMS' : lang === 'es' ? 'Notificaciones SMS' : 'SMS Notifications'}</p>
              <p className="text-xs text-gray-500">
                {smsOptedOut
                  ? (lang === 'sq' ? 'Nuk do të pranoni përditësime me SMS për takimet tuaja.' : lang === 'es' ? 'No recibirá actualizaciones SMS de sus citas.' : 'You will not receive SMS updates about your appointments.')
                  : (lang === 'sq' ? 'Pranoni kujtues të takimeve dhe alarme nisjeje teknikësh me SMS.' : lang === 'es' ? 'Reciba recordatorios de citas y alertas de técnicos por SMS.' : 'Receive appointment reminders and tech dispatch alerts via SMS.')}
              </p>
            </div>
          </div>
          <button
            onClick={toggleSmsPreference}
            disabled={smsBusy}
            className={`text-xs px-3 py-1.5 rounded font-bold tracking-widest border transition-all disabled:opacity-40 ${
              smsOptedOut
                ? 'bg-green-900/40 border-green-500/40 text-green-300 hover:bg-green-800/60'
                : 'bg-red-900/40 border-red-500/40 text-red-300 hover:bg-red-800/60'
            }`}
            data-testid="sms-toggle-btn"
          >
            {smsOptedOut
              ? (lang === 'sq' ? 'NDIZ' : lang === 'es' ? 'ACTIVAR' : 'TURN ON')
              : (lang === 'sq' ? 'FIK' : lang === 'es' ? 'DESACTIVAR' : 'TURN OFF')}
          </button>
        </div>
      </div>

      <div className="card bg-gray-900 border-gray-800">
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-white">{lang === 'sq' ? 'Kërkesat e Fundit' : lang === 'es' ? 'Solicitudes Recientes' : 'Recent Requests'}</h2>
          <Link to="/customer/new-request" className="btn-primary text-sm" data-testid="new-request-btn">
            <Plus className="w-4 h-4 inline mr-2" />
            {t('new_request')}
          </Link>
        </div>
        <div className="divide-y divide-gray-800">
          {requests.slice(0, 5).map((request) => (
            <div key={request.id} className="p-4 hover:bg-gray-800" data-testid={`request-item-${request.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono text-sm text-gray-500">{request.ticket_number}</p>
                  <p className="font-medium text-white">
                    <ServiceIcon type={request.service_type} className="w-4 h-4 inline mr-2 text-red-500" />
                    {request.service_type.replace('_', ' ').toUpperCase()}
                  </p>
                </div>
                <div className="text-right">
                  <StatusBadge status={request.status} />
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(request.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          ))}
          {requests.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              No service requests yet. Create your first request!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
